import { supabase } from '../lib/supabase';

export const commentService = {
  /**
   * Get all comment templates for the current user
   */
  async getTemplates() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('comment_templates')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(template => ({
        id: template?.id,
        userId: template?.user_id,
        name: template?.name,
        category: template?.category,
        content: template?.content,
        usageCount: template?.usage_count,
        successRate: parseFloat(template?.success_rate),
        createdAt: template?.created_at,
        updatedAt: template?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching comment templates:', error);
      throw error;
    }
  },

  /**
   * Create a new comment template
   */
  async createTemplate(templateData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('comment_templates')?.insert({
          user_id: user?.id,
          name: templateData?.name,
          category: templateData?.category,
          content: templateData?.content
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        name: data?.name,
        category: data?.category,
        content: data?.content,
        usageCount: data?.usage_count,
        successRate: parseFloat(data?.success_rate)
      };
    } catch (error) {
      console.error('Error creating comment template:', error);
      throw error;
    }
  },

  /**
   * Update a comment template
   */
  async updateTemplate(templateId, updates) {
    try {
      const { data, error } = await supabase?.from('comment_templates')?.update({
          name: updates?.name,
          category: updates?.category,
          content: updates?.content
        })?.eq('id', templateId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        category: data?.category,
        content: data?.content
      };
    } catch (error) {
      console.error('Error updating comment template:', error);
      throw error;
    }
  },

  /**
   * Delete a comment template
   */
  async deleteTemplate(templateId) {
    try {
      const { error } = await supabase?.from('comment_templates')?.delete()?.eq('id', templateId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting comment template:', error);
      throw error;
    }
  },

  /**
   * Get comment queue items
   */
  async getQueueItems(options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { status = null } = options;

      let query = supabase?.from('comment_queue')?.select(`
          *,
          template:comment_templates(id, name)
        `)?.eq('user_id', user?.id)?.order('scheduled_time', { ascending: true });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        userId: item?.user_id,
        templateId: item?.template_id,
        templateName: item?.template?.name,
        targetUser: item?.target_user,
        targetPlatform: item?.target_platform,
        commentText: item?.comment_text,
        scheduledTime: item?.scheduled_time,
        status: item?.status,
        retryCount: item?.retry_count,
        priority: item?.priority,
        createdAt: item?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching comment queue:', error);
      throw error;
    }
  },

  /**
   * Add comment to queue
   */
  async addToQueue(commentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('comment_queue')?.insert({
          user_id: user?.id,
          template_id: commentData?.templateId,
          target_user: commentData?.targetUser,
          target_platform: commentData?.targetPlatform,
          comment_text: commentData?.commentText,
          scheduled_time: commentData?.scheduledTime,
          priority: commentData?.priority || 'medium'
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        targetUser: data?.target_user,
        targetPlatform: data?.target_platform,
        commentText: data?.comment_text,
        scheduledTime: data?.scheduled_time,
        status: data?.status,
        priority: data?.priority
      };
    } catch (error) {
      console.error('Error adding to comment queue:', error);
      throw error;
    }
  },

  /**
   * Update queue item status
   */
  async updateQueueItem(itemId, updates) {
    try {
      const { data, error } = await supabase?.from('comment_queue')?.update({
          status: updates?.status,
          scheduled_time: updates?.scheduledTime,
          retry_count: updates?.retryCount
        })?.eq('id', itemId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        scheduledTime: data?.scheduled_time,
        retryCount: data?.retry_count
      };
    } catch (error) {
      console.error('Error updating queue item:', error);
      throw error;
    }
  },

  /**
   * Delete queue item
   */
  async deleteQueueItem(itemId) {
    try {
      const { error } = await supabase?.from('comment_queue')?.delete()?.eq('id', itemId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting queue item:', error);
      throw error;
    }
  }
};